import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { SearchField, TableLoading } from '../../../components';
import {
  SalesTeamMemberListFilter,
  SalesTeamMember,
  SalesTeamMemberState,
  clearSalesTeamMember,
  fetchSalesTeamMemberList,
} from '../../../../state/salesTeamMember';
import { PageFilter } from '../../../../state/app';
import { AppState } from '../../../../state/configureStore';
import { createResponsibilitySalesTeamMembers } from '../../../../state/responsibilitySalesTeamMember';
import { TablePageSize } from '../../../../constants';
import { uniqBy } from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialog: {
      minHeight: '53vh',
    },
    filterContainer: {
      display: 'flex',
      alignItems: 'center',
    },
  }),
);

interface AddResponsibilitySalesTeamMemberDialogProps {
  open: boolean;
  handleClose: () => void;
  responsibilityId: number;
}

const initialPaging = { pageNumber: 0, pageSize: TablePageSize[0] };

const AddResponsibilitySalesTeamMemberDialog: React.FC<
  AddResponsibilitySalesTeamMemberDialogProps
> = (props) => {
  const { open, handleClose, responsibilityId } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    items: SalesTeamMemberList,
    totalItems,
    loading,
  } = useSelector<AppState, SalesTeamMemberState>((state) => state.salesTeamMember);
  const [filters, setFilters] = useState<SalesTeamMemberListFilter>({
    'SalesTeam.Active.EqualsTo': true,
  });
  const [paging, setPaging] = useState<PageFilter>(initialPaging);
  const [selectedSalesTeamMembers, setSelectedSalesTeamMembers] = useState<
    SalesTeamMember[]
  >([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);

  useEffect(() => {
    setSelectAll(false);
  }, [filters, paging]);

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { target } = event;
    setFilters((prevState) => ({
      ...prevState,
      [target.name]: target.value,
    }));
    setPaging({
      ...paging,
      pageNumber: 0,
    });
  };

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  const resetForm = () => {
    handleClose();
    setSelectedSalesTeamMembers([]);
    setPaging({ ...initialPaging });
    setFilters({ 'SalesTeam.Active.EqualsTo': true });
  };

  const handleSubmit = () => {
    const submitValue = selectedSalesTeamMembers.map(
      (selectedSalesTeamMembers, index) => {
        return {
          salesTeamMemberId: selectedSalesTeamMembers.id,
          sequence: index + 1,
          responsibilityId: responsibilityId,
        };
      },
    );

    dispatch(createResponsibilitySalesTeamMembers(submitValue, resetForm));
  };

  useEffect(() => {
    dispatch(fetchSalesTeamMemberList(filters, paging));
    return function cleanUp() {
      dispatch(clearSalesTeamMember());
    };
  }, [dispatch, paging, filters]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth={true}
      disableBackdropClick={true}
    >
      <DialogTitle>Select SSO</DialogTitle>
      <DialogContent>
        <div className={classes.filterContainer}>
          <SearchField
            label="Sales Group"
            onChange={handleChangeFilter}
            filterKey="SalesTeam.SalesGroup.Name.StartsWith"
            filters={filters}
          />
          <SearchField
            label="Sales Team"
            onChange={handleChangeFilter}
            filterKey="SalesTeam.Name.startsWith"
            filters={filters}
          />
          <SearchField
            label="SSO"
            onChange={handleChangeFilter}
            filterKey="User.FullNameWithUserName.Contains"
            filters={filters}
          />
        </div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  onChange={(event: React.ChangeEvent<HTMLInputElement>, checked) => {
                    if (checked) {
                      const newList = [...selectedSalesTeamMembers].concat(
                        SalesTeamMemberList,
                      );
                      setSelectedSalesTeamMembers(uniqBy(newList, (c) => c.id!));
                    } else {
                      const isInCurrentList = (
                        salesTeamMember: SalesTeamMember,
                      ): boolean =>
                        !SalesTeamMemberList.find((s) => s.id === salesTeamMember.id);
                      setSelectedSalesTeamMembers(
                        selectedSalesTeamMembers.filter(isInCurrentList),
                      );
                    }
                    setSelectAll(checked);
                  }}
                  checked={selectAll}
                />
              </TableCell>
              <TableCell>No.</TableCell>
              <TableCell>Sales Group</TableCell>
              <TableCell>Sales Team</TableCell>
              <TableCell>Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableLoading isLoading={loading} colSpan={10}>
              {SalesTeamMemberList.map((salesTeamMember, index) => {
                const isCheckboxChecked =
                  selectedSalesTeamMembers.findIndex(
                    (selectedSalesTeamMember) =>
                      selectedSalesTeamMember.id === salesTeamMember.id,
                  ) !== -1;
                return (
                  <TableRow key={salesTeamMember.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                          checked,
                        ) => {
                          if (checked) {
                            setSelectedSalesTeamMembers([
                              ...selectedSalesTeamMembers,
                              salesTeamMember,
                            ]);
                          } else {
                            setSelectedSalesTeamMembers(
                              selectedSalesTeamMembers.filter(
                                (s) => s.id !== salesTeamMember.id,
                              ),
                            );
                          }
                        }}
                        checked={isCheckboxChecked}
                      />
                    </TableCell>
                    <TableCell>
                      {index + paging.pageSize * paging.pageNumber + 1}
                    </TableCell>
                    <TableCell>{salesTeamMember?.salesTeam?.salesGroup?.name}</TableCell>
                    <TableCell>{salesTeamMember?.salesTeam?.name}</TableCell>
                    <TableCell>{salesTeamMember?.user?.fullNameWithUserName}</TableCell>
                  </TableRow>
                );
              })}
            </TableLoading>
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[...TablePageSize]}
          component="div"
          count={totalItems}
          rowsPerPage={paging.pageSize}
          page={paging.pageNumber}
          onPageChange={handleChangePageNumber}
          onRowsPerPageChange={handleChangePageSize}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          Confirm
        </Button>
        <Button
          onClick={() => {
            resetForm();
          }}
          color="default"
          variant="contained"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddResponsibilitySalesTeamMemberDialog;
